import React, { useState } from "react";
import { QrCode, X, Copy, Check, Smartphone } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";

const NCC_LOGO = "/icons/icon-192.png";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.ncc.swms.manpowerdatabank";

/**
 * "Scan to Install" button — opens a modal showing a QR code pointing
 * to the SWMS-MPDB Google Play Store listing. Users scan with their phone
 * camera and install the app directly from Google Play.
 *
 * Props:
 *   variant: "navy" | "outline" | "ghost"
 *   size: "sm" | "md"
 *   className: extra classes
 *   label: button label (default "Scan to Install")
 *   url: override URL (defaults to the Play Store listing)
 */
export default function AppQrButton({
  variant = "outline",
  size = "md",
  className = "",
  label = "Scan to Install",
  url,
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const targetUrl = url || PLAY_STORE_URL;

  const sizeCls = size === "sm" ? "px-2.5 py-1.5 text-[11px]" : "px-3 py-2 text-xs";
  let variantCls = "";
  if (variant === "navy") {
    variantCls = "bg-ncc-gold text-ncc-navy hover:bg-ncc-gold2";
  } else if (variant === "outline") {
    variantCls = "border border-[#1e3a8a]/30 bg-white text-[#1e3a8a] hover:bg-[#eef2ff]";
  } else if (variant === "ghost") {
    variantCls = "bg-white/10 text-white border border-white/30 hover:bg-white/20";
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(targetUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      toast.error("Could not copy link");
    }
  }

  function downloadQr() {
    const canvas = document.querySelector('[data-testid="app-qr-canvas"] canvas');
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = "swms-mpdb-playstore-qr.png";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("QR code downloaded");
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="app-qr-btn"
        aria-label={label}
        className={`inline-flex items-center gap-2 rounded-md font-bold uppercase tracking-wider transition-all hover:-translate-y-0.5 ${sizeCls} ${variantCls} ${className}`}
      >
        <QrCode className="h-4 w-4" />
        <span className="hidden sm:inline">{label}</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setOpen(false)}
          data-testid="app-qr-modal"
        >
          <div
            className="w-full max-w-md rounded-2xl border-2 border-ncc-gold bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b-2 border-ncc-gold bg-ncc-navy px-5 py-3 rounded-t-2xl">
              <div className="flex items-center gap-2">
                <QrCode className="h-5 w-5 text-ncc-gold" />
                <h3 className="font-heading text-base font-bold uppercase tracking-wider text-white">
                  Scan to Install App
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="app-qr-close"
                className="rounded-md p-1 text-white/80 hover:bg-white/10 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-5">
              <div className="flex flex-col items-center">
                <div
                  className="rounded-2xl border-2 border-ncc-gold bg-white p-4 shadow-sm"
                  data-testid="app-qr-canvas"
                >
                  <QRCodeCanvas
                    value={targetUrl}
                    size={220}
                    level="H"
                    includeMargin={false}
                    imageSettings={{
                      src: NCC_LOGO,
                      height: 38,
                      width: 38,
                      excavate: true,
                    }}
                  />
                </div>
                <div className="mt-3 text-center text-[11px] font-bold uppercase tracking-wider text-[#1e3a8a]">
                  SWMS-MPDB · Get it on Google Play
                </div>
              </div>

              <ol className="space-y-2 text-sm text-slate-800">
                <li className="flex items-start gap-3">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-ncc-gold text-xs font-bold text-ncc-navy">1</span>
                  <span className="flex items-center gap-1">
                    Open the <Smartphone className="inline h-4 w-4 text-[#1e3a8a]" /> <span className="font-semibold">Camera</span> app on your phone.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-ncc-gold text-xs font-bold text-ncc-navy">2</span>
                  <span>Point it at the QR code and tap the link that appears.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-ncc-gold text-xs font-bold text-ncc-navy">3</span>
                  <span>Tap <span className="font-semibold">Install</span> on the <span className="font-semibold">Google Play</span> page that opens.</span>
                </li>
              </ol>

              <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <code className="flex-1 truncate font-mono text-[11px] text-slate-700" data-testid="app-qr-url">
                  {targetUrl}
                </code>
                <button
                  type="button"
                  onClick={copyLink}
                  data-testid="app-qr-copy"
                  className="inline-flex items-center gap-1 rounded-md bg-ncc-navy px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white hover:bg-[#152a63]"
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3 rounded-b-2xl">
              <button
                type="button"
                onClick={downloadQr}
                data-testid="app-qr-download"
                className="rounded-md border border-[#1e3a8a]/30 bg-white px-3 py-2 text-xs font-bold uppercase tracking-wider text-[#1e3a8a] hover:bg-[#eef2ff]"
              >
                Download QR
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="app-qr-ok"
                className="rounded-md bg-ncc-navy px-4 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-[#152a63]"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
